import React from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ArrowRight, Users, Award, Heart, Briefcase } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const team = [
  {
    name: 'Pierre Müller',
    role: 'Directeur Général',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80',
    expertise: 'Leadership & Stratégie'
  },
  {
    name: 'Sophie Dubois',
    role: 'Directrice Finance & Comptabilité',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
    expertise: 'Finance'
  },
  {
    name: 'Marc Weber',
    role: 'Directeur Technology',
    image: 'https://images.unsplash.com/photo-1758518731027-78a22c8852ec?w=400&q=80',
    expertise: 'IT & Digital'
  },
  {
    name: 'Anne-Marie Keller',
    role: 'Directrice Services Financiers',
    image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?w=400&q=80',
    expertise: 'Banking & Insurance'
  },
  {
    name: 'Thomas Bernasconi',
    role: 'Directeur Juridique',
    image: 'https://images.unsplash.com/photo-1573164574511-73c773193279?w=400&q=80',
    expertise: 'Legal & Compliance'
  },
  {
    name: 'Christine Favre',
    role: 'Directrice RH',
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=400&q=80',
    expertise: 'Human Resources'
  }
];

const EquipesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="bg-[#E30022] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-4">Nos équipes</h1>
          <p className="text-xl opacity-90">Des experts passionnés au service de votre réussite</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-gray-800 mb-6">L'expertise au c\u0153ur de notre ADN</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Chez Recrutement Suisse, nous avons réuni une équipe de consultants experts dans leurs domaines respectifs. 
            Chacun apporte une connaissance approfondie de son secteur et un réseau étendu de contacts qualifiés.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-800 mb-12 text-center">Notre équipe de direction</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <ResponsiveImage sizes="(min-width: 1280px) 384px, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-semibold">{member.name}</h3>
                    <p className="text-sm opacity-90">{member.role}</p>
                  </div>
                </div>
                <div className="p-4">
                  <span className="text-sm text-[#E30022] font-medium">{member.expertise}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-800 mb-12 text-center">Pourquoi rejoindre notre équipe ?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#E30022]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 text-[#E30022]" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Esprit d'équipe</h3>
              <p className="text-gray-600 text-sm">Une culture collaborative et bienveillante</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#E30022]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-7 w-7 text-[#E30022]" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Excellence</h3>
              <p className="text-gray-600 text-sm">Des standards élevés et une formation continue</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#E30022]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-7 w-7 text-[#E30022]" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Bien-être</h3>
              <p className="text-gray-600 text-sm">Équilibre vie professionnelle et personnelle</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#E30022]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="h-7 w-7 text-[#E30022]" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Évolution</h3>
              <p className="text-gray-600 text-sm">Des opportunités de carrière passionnantes</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Envie de nous rejoindre ?</h2>
          <p className="text-gray-400 mb-8">Découvrez nos offres d'emploi et rejoignez une équipe passionnée.</p>
          <a 
            href="/carrieres"
            className="inline-flex items-center gap-2 bg-[#E30022] text-white px-8 py-4 rounded hover:bg-[#c7001d] transition-colors font-medium group"
          >
            Voir nos offres
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EquipesPage;
